import { z } from 'zod';
import { rectSchema } from './layout';
import { entityTypeSchema } from './registry';

/**
 * A row of `public.entities` as the staff review screen selects it: fixed since the version that first found it
 * (`first_script_version_id`). `extra` is left open here: the registry's schema validates it when it is written, and
 * the screen shows only the fields the registry describes.
 */
export const entityRowSchema = z.object({
  id: z.uuid(),
  script_id: z.uuid(),
  type: entityTypeSchema,
  key: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  first_script_version_id: z.uuid(),
  extra: z.record(z.string(), z.unknown()),
});
export type EntityRow = z.infer<typeof entityRowSchema>;

/**
 * A row of `public.entity_versions`: what one version says of an entity. Each field is null when there is
 * nothing to say (no points; nothing material changed).
 */
export const entityVersionRowSchema = z.object({
  id: z.uuid(),
  entity_id: z.uuid(),
  script_version_id: z.uuid(),
  defamatory_points: z.array(z.string()).nullable(),
  version_updates: z.string().nullable(),
});
export type EntityVersionRow = z.infer<typeof entityVersionRowSchema>;

/**
 * A row of `public.script_references` as the review screen selects it: the span in the version's text and its
 * rectangles on the PDF (fractions of the page, origin top-left). `page` is the page of the first rectangle.
 */
export const scriptReferenceRowSchema = z.object({
  id: z.uuid(),
  entity_id: z.uuid(),
  page: z.number().int().positive(),
  matched_text: z.string(),
  start_line: z.number().int().positive(),
  start_char: z.number().int().nonnegative(),
  end_line: z.number().int().positive(),
  end_char: z.number().int().nonnegative(),
  rects: z.array(rectSchema).min(1),
});
export type ScriptReferenceRow = z.infer<typeof scriptReferenceRowSchema>;

/** How an entity stands in one version on the review screen. */
export type ReviewEntityState = 'new' | 'updated' | 'unchanged';

/**
 * `new` in the version that first found the entity; `updated` in a later one whose row says what changed or gives
 * defamatory points; `unchanged` when the later version only mentions it.
 */
export function reviewEntityStateFor(
  entity: Pick<EntityRow, 'first_script_version_id'>,
  row: Pick<EntityVersionRow, 'script_version_id' | 'defamatory_points' | 'version_updates'>,
): ReviewEntityState {
  if (row.script_version_id === entity.first_script_version_id) return 'new';
  return row.version_updates || row.defamatory_points ? 'updated' : 'unchanged';
}
